// ExamCard.tsx
import React from "react";
import { Card, CardContent, CardActions, Box, Stack, Typography, Chip, Button } from "@mui/material";
import { ArrowForward } from "@mui/icons-material";
import { useTranslation } from "react-i18next";
import type { QuizBackground } from "@/types/Quiz.types";
import type { PortalBrand, PortalThemeConfig } from "@/types/Portal.types";

interface ExamCardProps {
  item: QuizBackground;
  brand: PortalBrand;
  themeConfig?: PortalThemeConfig;
  actionText?: string | ((item: QuizBackground) => string);
  onActionClick: (item: QuizBackground) => void;
  isDisabled: boolean;
}

export const ExamCard: React.FC<ExamCardProps> = ({
  item,
  brand,
  themeConfig,
  actionText,
  onActionClick,
  isDisabled = false,
}) => {
  const { t } = useTranslation();
  const IconComponent = brand.icon;

  const defaultActionText = t("components.examCard.exploreModules");
  const resolvedActionText = typeof actionText === "function" ? actionText(item) : actionText || defaultActionText;

  return (
    <Card
      elevation={0}
      sx={{
        height: "100%",
        display: "flex",
        flexDirection: "column",
        borderRadius: 2,
        border: "1px solid",
        borderColor: themeConfig?.borderColor || "divider",
        bgcolor: themeConfig?.cardBg || "background.paper",
        position: "relative",
        overflow: "hidden",
        transition: "all 0.4s cubic-bezier(0.16, 1, 0.3, 1)",
        "&:hover": {
          borderColor: "transparent",
          boxShadow: (theme) =>
            theme.palette.mode === "light"
              ? `0 24px 48px -12px rgba(15, 23, 42, 0.08), 0 0 0 1px ${brand.accentColor}30`
              : `0 24px 48px -12px rgba(0, 0, 0, 0.5), 0 0 0 1px ${brand.accentColor}40`,
          transform: "translateY(-4px)",
          background: brand.gradient,
          "& .brand-icon-box": {
            bgcolor: "background.paper",
            boxShadow: (theme) =>
              theme.palette.mode === "light" ? "0 8px 20px -4px rgba(0,0,0,0.05)" : "0 8px 20px -4px rgba(0,0,0,0.4)",
            transform: "scale(1.05) rotate(3deg)",
          },
          "& .brand-glow-orb": {
            transform: "scale(1.4)",
            opacity: 0.8,
          },
          "& .action-btn-arrow": {
            transform: "translateX(4px)",
          },
        },
      }}
    >
      {/* Background Glow Effect */}
      <Box
        className="brand-glow-orb"
        sx={{
          position: "absolute",
          top: -40,
          right: -40,
          width: 120,
          height: 120,
          borderRadius: "50%",
          bgcolor: brand.accentColor,
          filter: "blur(40px)",
          opacity: 0.2,
          pointerEvents: "none",
          transition: "all 0.5s ease",
        }}
      />

      {/* Main Content */}
      <CardContent sx={{ p: 3.5, flexGrow: 1, position: "relative", zIndex: 1 }}>
        {/* Header Row */}
        <Stack direction="row" sx={{ mb: 3, justifyContent: "space-between", alignItems: "center" }}>
          <Box
            className="brand-icon-box"
            sx={{
              p: 1.2,
              bgcolor: "background.default",
              borderRadius: 2,
              display: "flex",
              border: "1px solid",
              borderColor: "divider",
              transition: "all 0.3s ease",
            }}
          >
            <IconComponent sx={{ fontSize: 26, color: brand.accentColor, transition: "transform 0.3s" }} />
          </Box>
          {item.tags?.slice(0, 1).map((t, idx) => (
            <Chip
              key={idx}
              label={t.label}
              color={t.color}
              size="small"
              sx={{
                height: 22,
                fontSize: "0.725rem",
                fontWeight: 600,
                ...(!t.color && {
                  bgcolor: brand.chipBg || `${brand.accentColor}12`,
                  color: brand.chipColor || brand.accentColor,
                  border: `1px solid ${brand.accentColor}20`,
                }),
              }}
            />
          ))}
        </Stack>

        {/* Title & Description */}
        <Box sx={{ mb: 2 }}>
          <Typography
            variant="h6"
            component="h2"
            sx={{
              fontWeight: 800,
              color: themeConfig?.textColorPrimary || "text.primary",
              fontSize: "1.15rem",
              lineHeight: 1.3,
              mb: 0.5,
            }}
          >
            {item.title}
          </Typography>
          <Typography
            variant="caption"
            sx={{
              color: themeConfig?.textColorSecondary || "text.secondary",
              fontWeight: 500,
              display: "block",
            }}
          >
            {item.description}
          </Typography>
        </Box>

        {/* Overview */}
        <Typography variant="body2" sx={{ color: "text.secondary", fontSize: "0.85rem", lineHeight: 1.6, mb: 3.5 }}>
          {item.overview}
        </Typography>

        {/* Key Specs */}
        {item.specs && item.specs.length > 0 && (
          <Stack
            direction="row"
            spacing={2}
            sx={{
              pt: 2.5,
              borderTop: "1px solid",
              borderColor: "divider",
            }}
          >
            {item.specs.map((spec, idx) => (
              <Box key={idx} sx={{ flex: 1 }}>
                <Typography
                  variant="caption"
                  sx={{
                    color: "text.secondary",
                    display: "block",
                    fontSize: "0.68rem",
                    textTransform: "uppercase",
                    fontWeight: 600,
                    letterSpacing: "0.02em",
                    opacity: 0.8,
                  }}
                >
                  {spec.title}
                </Typography>
                <Typography variant="body2" sx={{ fontWeight: 800, color: "text.primary", fontSize: "0.9rem", mt: 0.25 }}>
                  {spec.description}
                </Typography>
              </Box>
            ))}
          </Stack>
        )}
      </CardContent>

      {/* Action Footer */}
      <CardActions sx={{ px: 3.5, pb: 3.5, pt: 0, position: "relative", zIndex: 1 }}>
        <Button
          disabled={isDisabled}
          variant="contained"
          disableElevation
          fullWidth
          endIcon={<ArrowForward className="action-btn-arrow" sx={{ fontSize: "0.95rem", transition: "transform 0.2s" }} />}
          sx={{
            textTransform: "none",
            py: 1.3,
            borderRadius: 2,
            fontWeight: 700,
            bgcolor: "text.primary",
            color: "background.paper",
            fontSize: "0.85rem",
            transition: "all 0.2s ease",
            "&:hover": {
              bgcolor: brand.accentColor,
              color: "#ffffff",
              boxShadow: `0 8px 20px -6px ${brand.accentColor}`,
            },
          }}
          onClick={() => onActionClick(item)}
        >
          {resolvedActionText}
        </Button>
      </CardActions>
    </Card>
  );
};
